import {FC, useEffect, useState} from 'react'
import {Button, Form, Row, Col} from 'react-bootstrap'
import AsyncSelect from 'react-select/async'
import type {CSSObjectWithLabel, GroupBase, ControlProps, OptionProps} from 'react-select'
import Table from '../../common/Table'
import TableHeader from '../../common/TableHeader'
import {useDispatch, useSelector} from 'react-redux'
import {AppDispatch, RootState} from '../../../../../redux/store'
import {getReportListApi} from '../../../../../redux/slices/ReportSlice'
import {exportReportExcel, ReportFilterParams} from '../../../../../services/ReportService'
import {getClientList} from '../../../../../services/ClientService'
import {toast} from 'react-toastify'

interface ClientOption {
  value: number
  label: string
}

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    approved: 'success',
    pending: 'warning',
    rejected: 'danger',
  }
  return (
    <span className={`badge badge-light-${map[status] ?? 'secondary'} fw-bold`}>
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : '—'}
    </span>
  )
}

const actionBadge = (actionStatus: string) => (
  <span
    className={`badge badge-light-${actionStatus === 'Credit' ? 'success' : 'danger'} fw-bold`}
  >
    {actionStatus || '—'}
  </span>
)

const dash = (v: any) => (v != null && String(v).trim() !== '' ? v : '—')

const ReportTable: FC = () => {
  const dispatch = useDispatch<AppDispatch>()

  const reportData = useSelector(
    (state: RootState) => state.report.ReportList?.data?.data?.data || []
  )
  const reportTotal = useSelector(
    (state: RootState) => state.report.ReportList?.data?.data?.total || 0
  )

  const [loading, setLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(50)

  // Filter state
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [minPoint, setMinPoint] = useState<number | ''>('')
  const [maxPoint, setMaxPoint] = useState<number | ''>('')
  const [minMoney, setMinMoney] = useState<number | ''>('')
  const [maxMoney, setMaxMoney] = useState<number | ''>('')
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null)
  const [status, setStatus] = useState('')
  const [actionId, setActionId] = useState<number | ''>('')

  // Active filters that drive the actual fetch (applied on button click)
  const [activeFilters, setActiveFilters] = useState<ReportFilterParams>({})

  const buildParams = (): ReportFilterParams => ({
    ...(startDate && {start_date: startDate}),
    ...(endDate && {end_date: endDate}),
    ...(minPoint !== '' && {min_point: minPoint}),
    ...(maxPoint !== '' && {max_point: maxPoint}),
    ...(minMoney !== '' && {min_money: minMoney}),
    ...(maxMoney !== '' && {max_money: maxMoney}),
    ...(selectedClient && {client_id: String(selectedClient.value)}),
    ...(status && {status}),
    ...(actionId !== '' && {action_id: actionId}),
  })

  const fetchReports = (filters: ReportFilterParams = activeFilters) => {
    setLoading(true)
    dispatch(
      getReportListApi({
        ...filters,
        page: currentPage,
        pageSize: rowsPerPage,
      })
    )
      .then(() => setLoading(false))
      .catch(() => {
        setLoading(false)
        toast.error('Failed to fetch report data')
      })
  }

  useEffect(() => {
    fetchReports()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage, activeFilters])

  const handleApplyFilters = () => {
    const params = buildParams()
    setActiveFilters(params)
    setCurrentPage(1)
  }

  const handleResetFilters = () => {
    setStartDate('')
    setEndDate('')
    setMinPoint('')
    setMaxPoint('')
    setMinMoney('')
    setMaxMoney('')
    setSelectedClient(null)
    setStatus('')
    setActionId('')
    setActiveFilters({})
    setCurrentPage(1)
  }

  const handleExportExcel = async () => {
    setExportLoading(true)
    try {
      const response = await exportReportExcel(activeFilters)
      const downloadUrl = response.data?.data?.download_url
      if (downloadUrl) {
        window.open(downloadUrl, '_blank')
      } else {
        toast.error('Export failed: no download URL received')
      }
    } catch {
      toast.error('Failed to export report')
    } finally {
      setExportLoading(false)
    }
  }

  const handlePageChange = (page: number) => setCurrentPage(page)
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setRowsPerPage(newPerPage)
    setCurrentPage(page)
  }

  const loadClientOptions = async (inputValue: string): Promise<ClientOption[]> => {
    try {
      const response = await getClientList(1, 50, inputValue)
      const list = response.data?.data?.data || response.data?.data || []
      return list.map((c: any) => ({value: c.id, label: `${c.name} (${c.phone || c.id})`}))
    } catch {
      return []
    }
  }

  const filterContent = (
    <div style={{width: '100%'}}>
      <Row className='g-2 mb-2'>
        <Col xs={6}>
          <Form.Label className='fw-semibold fs-7 mb-1'>Start Date</Form.Label>
          <Form.Control
            type='date'
            size='sm'
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col xs={6}>
          <Form.Label className='fw-semibold fs-7 mb-1'>End Date</Form.Label>
          <Form.Control
            type='date'
            size='sm'
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
      </Row>

      <Row className='g-2 mb-2'>
        <Col xs={6}>
          <Form.Label className='fw-semibold fs-7 mb-1'>Min Points</Form.Label>
          <Form.Control
            type='number'
            size='sm'
            placeholder='0'
            value={minPoint}
            onChange={(e) => setMinPoint(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </Col>
        <Col xs={6}>
          <Form.Label className='fw-semibold fs-7 mb-1'>Max Points</Form.Label>
          <Form.Control
            type='number'
            size='sm'
            placeholder='0'
            value={maxPoint}
            onChange={(e) => setMaxPoint(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </Col>
      </Row>

      <Row className='g-2 mb-2'>
        <Col xs={6}>
          <Form.Label className='fw-semibold fs-7 mb-1'>Min Money (₹)</Form.Label>
          <Form.Control
            type='number'
            size='sm'
            placeholder='0.00'
            value={minMoney}
            onChange={(e) => setMinMoney(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </Col>
        <Col xs={6}>
          <Form.Label className='fw-semibold fs-7 mb-1'>Max Money (₹)</Form.Label>
          <Form.Control
            type='number'
            size='sm'
            placeholder='0.00'
            value={maxMoney}
            onChange={(e) => setMaxMoney(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </Col>
      </Row>

      <Row className='g-2 mb-2'>
        <Col xs={12}>
          <Form.Label className='fw-semibold fs-7 mb-1'>Client</Form.Label>
          <AsyncSelect<ClientOption>
            cacheOptions
            defaultOptions
            loadOptions={loadClientOptions}
            value={selectedClient}
            onChange={(option: ClientOption | null) => setSelectedClient(option)}
            placeholder='Search by name or phone...'
            isClearable
            styles={{
              control: (base: CSSObjectWithLabel, _: ControlProps<ClientOption, false, GroupBase<ClientOption>>) => ({...base, minHeight: '34px', fontSize: '13px'}),
              option: (base: CSSObjectWithLabel, _: OptionProps<ClientOption, false, GroupBase<ClientOption>>) => ({...base, fontSize: '13px'}),
            }}
          />
        </Col>
      </Row>

      <Row className='g-2 mb-3'>
        <Col xs={6}>
          <Form.Label className='fw-semibold fs-7 mb-1'>Status</Form.Label>
          <Form.Select size='sm' value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value=''>All</option>
            <option value='pending'>Pending</option>
            <option value='approved'>Approved</option>
            <option value='rejected'>Rejected</option>
          </Form.Select>
        </Col>
        <Col xs={6}>
          <Form.Label className='fw-semibold fs-7 mb-1'>Action</Form.Label>
          <Form.Select
            size='sm'
            value={actionId}
            onChange={(e) => setActionId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value=''>All</option>
            <option value={1}>Credit</option>
            <option value={2}>Debit</option>
          </Form.Select>
        </Col>
      </Row>

      <div className='d-flex justify-content-end gap-2'>
        <Button variant='light' size='sm' onClick={handleResetFilters}>
          Reset
        </Button>
        <Button variant='primary' size='sm' onClick={handleApplyFilters}>
          Apply
        </Button>
      </div>
    </div>
  )

  const columnData = [
    {
      name: 'S.No',
      cell: (_row: any, index: number) => (currentPage - 1) * rowsPerPage + index + 1,
      width: '70px',
      sortable: false,
    },
    {
      name: 'Client',
      cell: (row: any) => (
        <div className='d-flex flex-column py-2'>
          <span className='text-dark fw-bold fs-7'>{dash(row?.client_detail?.name)}</span>
          <span className='text-muted fw-semibold fs-8'>{dash(row?.client_detail?.phone)}</span>
        </div>
      ),
      minWidth: '160px',
      sortable: false,
    },
    {
      name: 'Address',
      cell: (row: any) => {
        const addr = row?.client_detail?.address
        if (!addr) return <span className='text-muted'>—</span>
        return (
          <span className='text-muted fw-semibold fs-7'>
            {addr.length > 20 ? addr.substring(0, 20) + '…' : addr}
          </span>
        )
      },
      minWidth: '140px',
      sortable: false,
    },
    {
      name: 'Role',
      cell: (row: any) => {
        const roleId = row?.client_detail?.role_id
        const roleName =
          roleId === 1 ? 'Dealer' : roleId === 2 ? 'Carpenter' : dash(row?.client_detail?.role?.name)
        return <span className='text-dark fw-semibold fs-7'>{roleName}</span>
      },
      minWidth: '100px',
      sortable: false,
    },
    {
      name: 'Coupon',
      cell: (row: any) => (
        <div className='d-flex flex-column py-2'>
          <span className='text-dark fw-semibold fs-7'>{dash(row?.coupon_detail?.name)}</span>
          <span className='text-muted fw-semibold fs-8'>{dash(row?.coupon_code)}</span>
        </div>
      ),
      minWidth: '140px',
      sortable: false,
    },
    {
      name: 'Gift',
      cell: (row: any) => (
        <span className='text-dark fw-semibold fs-7'>{dash(row?.gift_gallery_detail?.name)}</span>
      ),
      minWidth: '120px',
      sortable: false,
    },
    {
      name: 'Action',
      cell: (row: any) => actionBadge(row?.action_status),
      minWidth: '90px',
      sortable: false,
    },
    {
      name: 'Points',
      cell: (row: any) => (
        <span className='text-dark fw-semibold fs-7'>{dash(row?.points)}</span>
      ),
      minWidth: '80px',
      sortable: false,
    },
    {
      name: 'Money (₹)',
      cell: (row: any) => (
        <span className='text-dark fw-semibold fs-7'>
          {row?.money != null ? Number(row.money).toFixed(2) : '—'}
        </span>
      ),
      minWidth: '100px',
      sortable: false,
    },
    {
      name: 'Status',
      cell: (row: any) => statusBadge(row?.status),
      minWidth: '100px',
      sortable: false,
    },
    {
      name: 'Date',
      cell: (row: any) =>
        row?.created_at
          ? new Date(row.created_at).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : '—',
      minWidth: '120px',
      sortable: false,
    },
  ]

  return (
    <div className='card shadow-sm'>
      <TableHeader
        search={undefined}
        showFilter={true}
        filterContent={filterContent}
        additionalButtons={[
          {
            icon: exportLoading ? 'bi-hourglass-split' : 'bi-file-earmark-excel',
            tooltip: 'Export Excel',
            onClick: handleExportExcel,
          },
        ]}
      />

      <div className='card-body p-0 shadow-sm'>
        <Table
          data={reportData}
          columns={columnData}
          isLoading={loading}
          handlePerRowsChange={handlePerRowsChange}
          handlePageChange={handlePageChange}
          totalRows={reportTotal}
          perPageData={rowsPerPage}
          showPagination={true}
          onRowClicked={() => {}}
          selectableRows={false}
          onSelectedRowsChange={() => {}}
        />
      </div>
    </div>
  )
}

export default ReportTable
